

























































































































import Vue, { PropType } from 'vue'
import XCard from '@/components/atoms/Card/XCard.vue'
import XCardBody from '@/components/atoms/Card/XCardBody.vue'
import XForm from '@/components/atoms/Form/XForm.vue'
import XButton from '@/components/atoms/Button/XButton.vue'
import XCheckbox from '@/components/atoms/Checkbox/XCheckbox.vue'
import Store, { Input as StoreInput } from './_Partial/Store.vue'
import Recipient, {
  Input as RecipientInput,
  toInput as toRecipientInput,
} from './_Partial/Recipient.vue'
import DeliveryOptions, {
  Input as DeliveryOptionsInput,
} from './_Partial/DeliveryOptions.vue'
import ProductList, {
  Input as ProductListInput,
} from './_Partial/ProductList.vue'
import Introduction from './_Partial/Introduction.vue'
import ShippingDeliveryImage from './_Partial/ShippingDeliveryImage.vue'
import PartnerPaymentMethod from './_Partial/PartnerPaymentMethod.vue'
import ZipValidator from '@/plugins/validations/ZipValidator'
import {
  AgentSalesDestination,
  CreateAgentSalesOrderInput,
  CreditCard,
  Discount,
  Partner,
  PartnerServiceContract,
  PaymentMethod,
  Query,
  SalesProduct,
} from '@/types/generated/graphql'
import { showToast } from '@/packages/toast'
import FfpAgentOrderConfirmation from './Confirmation/FfpAgentOrderConfirmation.vue'
import AgentDestinationSave from '@/components/molecules/FfpAgent/Destination/AgentDestinationSave.vue'
import AgentDestinationPicker from '@/components/molecules/FfpAgent/Destination/AgentDestinationPicker.vue'
import CREDIT_CARD_QUERY from '@/graphql/user/creditCard.gql'
import SALES_PRODUCTS_QUERY from '@/graphql/product/salesProducts.gql'
import DISCOUNT_QUERY from '@/graphql/product/discount.gql'

type Data = {
  input: {
    productList: ProductListInput
    store: StoreInput
    recipient: RecipientInput
    deliveryOptions: DeliveryOptionsInput
    agreeToPolicy: boolean
    discountCode?: string
  }
  creditCard: CreditCard | null
  salesProducts: SalesProduct[]
  selectedDestination: AgentSalesDestination | null
  discount: Discount | null
}

type Props = {
  partner: Partner
  serviceContract: PartnerServiceContract
}

type Computed = {
  canOrder: boolean
  productList: ProductListInput
  store: StoreInput
  recipient: RecipientInput
  deliveryOptions: DeliveryOptionsInput
  computedInput: CreateAgentSalesOrderInput
}

type Methods = {
  onSubmitSuccess: () => void
  onSubmit: () => void
  zipCodeIfValid: (errors: Record<string, string[]>) => string | undefined
  reset: () => void
  onClickDestinationPicker: () => void
  onSelectDestination: (destination: AgentSalesDestination) => void
  onCloseDestinationSaveDialog: () => void
  getDiscount: (discountId: string) => Promise<Discount>
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'FfpAgentOrder',
  components: {
    XCard,
    XCardBody,
    XForm,
    XButton,
    XCheckbox,
    Recipient,
    DeliveryOptions,
    PartnerPaymentMethod,
    ProductList,
    Introduction,
    ShippingDeliveryImage,
    FfpAgentOrderConfirmation,
    AgentDestinationSave,
    AgentDestinationPicker,
    Store,
  },
  props: {
    partner: {
      type: Object as PropType<Partner>,
      required: true,
    },
    serviceContract: {
      type: Object as PropType<PartnerServiceContract>,
    },
  },
  computed: {
    canOrder(): boolean {
      return (
        this.partner.paymentMethod === PaymentMethod.Bank ||
        (this.partner.paymentMethod === PaymentMethod.Credit &&
          this.creditCard !== null)
      )
    },
    productList: {
      get() {
        return this.input.productList
      },
      set(productList) {
        this.input.productList = productList
      },
    },
    store: {
      get() {
        return this.input.store
      },
      set(store) {
        this.input.store = store
      },
    },
    recipient: {
      get() {
        return this.input.recipient
      },
      set(recipient) {
        this.input.recipient = recipient
      },
    },
    deliveryOptions: {
      get() {
        return this.input.deliveryOptions
      },
      set(deliveryOptions) {
        this.input.deliveryOptions = deliveryOptions
      },
    },
    computedInput() {
      // GraphQL mutation input
      const {
        productList,
        store,
        recipient,
        deliveryOptions,
        discountCode,
      } = this.input
      return {
        consumerPartnerId: this.serviceContract.partnerId,
        servicePlanId: this.serviceContract.servicePlanId,
        paymentMethod: this.partner.paymentMethod,
        ...store,
        ...recipient,
        ...deliveryOptions,
        ...productList,
        discountCode,
      } as CreateAgentSalesOrderInput
    },
  },
  data() {
    return {
      input: {
        store: {
          storeId: undefined,
        },
        recipient: {
          firstName: undefined,
          lastName: undefined,
          firstNameKana: undefined,
          lastNameKana: undefined,
          zipCode: undefined,
          prefId: undefined,
          address1: undefined,
          address2: undefined,
          phone: undefined,
          email: undefined,
        },
        deliveryOptions: {
          deliveryFrequency: undefined,
          numberOfDeliveries: undefined,
          deliveryDayOfWeek: undefined,
          deliveryTimeSlot: undefined,
        },
        productList: {
          orderItems: [],
        },
        agreeToPolicy: false,
        discountCode: undefined,
      },
      creditCard: null,
      salesProducts: [],
      selectedDestination: null,
      discount: null,
    }
  },
  methods: {
    onSubmitSuccess() {
      showToast(this.$tc('ffpAgentHome.orderCompleted'))

      const destinationSaveDialog = this.$refs.destinationSaveDialog as any
      if (destinationSaveDialog.hasUpdate()) {
        destinationSaveDialog.open()
      } else {
        this.reset()
      }
    },
    onSubmit() {
      const { confirmation } = this.$refs as any
      confirmation.open()
    },
    zipCodeIfValid(errors) {
      const { zipCode } = this.input.recipient
      if (errors.zipcode && errors.zipcode.length) {
        return undefined
      }
      if (!ZipValidator.validate?.(zipCode, [])) {
        return undefined
      }
      return zipCode
    },
    reset() {
      const { data } = this.$options as any
      Object.assign(this.$data.input, data?.apply(this).input)
      const { form } = this.$refs as any
      this.selectedDestination = null
      this.discount = null
      form.reset()
    },
    onClickDestinationPicker() {
      if (this.store.storeId === undefined) {
        showToast(this.$tc('ffpAgentHome.selectStoreOfDestination'))
        return
      }
      const destinationPicker = this.$refs.destinationPicker as any
      destinationPicker.open()
    },
    async onSelectDestination(destination: AgentSalesDestination) {
      this.input.recipient = toRecipientInput(destination)
      this.input.discountCode = destination.discountCode || undefined
      this.selectedDestination = destination
      this.discount = null
      if (destination?.code?.isAvailable) {
        this.discount = await this.getDiscount(destination.code.discountId)
      }
    },
    onCloseDestinationSaveDialog() {
      this.reset()
    },
    async getDiscount(discountId: string) {
      const { data } = await this.$apollo.query<Query>({
        query: DISCOUNT_QUERY,
        variables: { discountId: discountId },
      })
      return data.discount
    },
  },
  apollo: {
    creditCardQuery: {
      query: CREDIT_CARD_QUERY,
      fetchPolicy: 'network-only',
      update({ creditCard }) {
        this.creditCard = creditCard
      },
    },
    salesProducts: {
      query: SALES_PRODUCTS_QUERY,
      fetchPolicy: 'network-only',
      variables() {
        const { partnerServiceContractId } = this.serviceContract
        return { input: { partnerServiceContractId }, withMenu: false }
      },
    },
  },
})
